// landing/sections/FiveRulesSection.tsx — the five categories, sourced from the
// policy document itself.
//
// The headings and one-line summaries are read straight out of the Community
// Guidelines data (the five sections whose ids are listed below), not
// hand-copied — so the landing page cannot drift from what the rules actually
// say, and a test pins the correspondence. The legal-floor caveat is one
// sentence here because the Guidelines devote a whole section to keeping it
// from being mistaken for a sixth category.

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Panel } from '@/ui';
import { communityGuidelines } from '@/core/data/policies/guidelines';

/** The five category sections of the Community Guidelines, in document order. */
export const FIVE_RULE_SECTION_IDS = [
  'direct-threats',
  'terrorism',
  'scams',
  'fraud',
  'coordinated-abuse',
] as const;

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Intro = styled.p`
  margin: 0;
  max-width: 62ch;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Rules = styled.ol`
  list-style: none;
  counter-reset: rule;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Rule = styled(Panel).attrs({ as: 'li' })`
  counter-increment: rule;
  display: flex;
  align-items: baseline;
  gap: 14px;
  padding: 14px 16px;

  &::before {
    content: counter(rule, decimal-leading-zero);
    font-family: ${({ theme }) => theme.font.mono};
    font-variant-numeric: tabular-nums;
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.color.accentCyan};
    flex-shrink: 0;
  }
`;

const RuleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RuleName = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RuleSummary = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.55;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Caveat = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textFaint};

  a {
    color: ${({ theme }) => theme.color.accentCyan};
    text-decoration: none;
    &:hover { text-decoration: underline; }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.color.focusRing};
      outline-offset: 2px;
    }
  }
`;

export function FiveRulesSection() {
  const sections = FIVE_RULE_SECTION_IDS.map(
    (id) => communityGuidelines.sections.find((s) => s.id === id)!,
  );

  return (
    <Wrap aria-labelledby="landing-rules-heading">
      <SectionHeading id="landing-rules-heading">The five rules</SectionHeading>
      <Intro>
        The Council can act on exactly five kinds of content. Each rule is published with
        what it covers and, just as deliberately, what it does not.
      </Intro>
      <Rules>
        {sections.map((s) => (
          <Rule key={s.id}>
            <RuleText>
              <RuleName>{s.heading}</RuleName>
              <RuleSummary>{s.summary}</RuleSummary>
            </RuleText>
          </Rule>
        ))}
      </Rules>
      <Caveat>
        One narrow exception sits outside the five: content that is unlawful to host is
        removed directly, without a vote. It is not a sixth category, and the{' '}
        <Link to="/policies">rules explain exactly what it covers</Link>.
      </Caveat>
    </Wrap>
  );
}
